import { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  LinearProgress, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import CloseIcon from "@mui/icons-material/Close";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import API from "../api";
import LogSidePanel from "./LogSidePanel";
import { colors, palette } from "../theme";

dayjs.extend(utc);
dayjs.extend(timezone);

const RANGES = [
  { value: "24h", label: "Last 24 hours", hours: 24 },
  { value: "7d", label: "Last 7 days", hours: 24 * 7 },
  { value: "15d", label: "Last 15 days", hours: 24 * 15 },
];

const MIN_EVENTS = [10, 20, 50, 100, 500];

const TYPES = {
  fan_out: { label: "One source, many destinations", short: "Source to many", icon: CallSplitIcon, color: "#60adb3" },
  fan_in: { label: "Many sources, one destination", short: "Many to destination", icon: CallMergeIcon, color: "#b39ddb" },
  pair: { label: "Repeated source/destination pair", short: "Repeated pair", icon: SyncAltIcon, color: "#E0A030" },
  widespread: { label: "Detail seen everywhere", short: "Widespread detail", icon: BlurOnIcon, color: "#f28b82" },
};

const SEVERITY_LABEL = { 2: "Alert", 3: "Movement" };
const BAR_COLOR = palette.primary.main;
const TICK = { fontSize: 10, fill: colors.grey[500] };
const MONO = "ui-monospace, Consolas, monospace";

const nf = new Intl.NumberFormat("en-US");
const pct = (v) => `${(v * 100).toFixed(v >= 0.1 || v === 0 ? 0 : 1)}%`;
const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

const patternKey = (p) => `${p.signature}|${p.src_ip}|${p.dest_ip}`;

const Mono = ({ children }) => <Box component="span" sx={{ fontFamily: MONO, fontSize: "0.85em" }}>{children}</Box>;

const Panel = ({ title, subtitle, children, sx }) => (
  <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)", backgroundColor: "#232323", minWidth: 0, ...sx }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{subtitle}</Typography>}
    {children}
  </Box>
);

const Empty = ({ height = 120 }) => (
  <Box sx={{ height, display: "grid", placeItems: "center", color: "text.secondary", fontSize: 12 }}>No data</Box>
);

// Andamento nel tempo: barre orarie fino a 3 giorni, giornaliere oltre (lo decide il server).
const TimelineChart = ({ points, unit, timeZone, height = 180, color = BAR_COLOR }) => {
  if (!points?.length) return <Empty height={height} />;
  const fmt = unit === "hour" ? "DD/MM HH:00" : "DD/MM";
  const labels = points.map((p) => dayjs(p.t).tz(timeZone).format(fmt));
  return (
    <BarChart
      height={height}
      series={[{ data: points.map((p) => p.n), color, label: "Events", valueFormatter: (v) => nf.format(v ?? 0) }]}
      xAxis={[{ scaleType: "band", data: labels, tickLabelStyle: TICK, tickLabelInterval: (_, i) => i % Math.ceil(labels.length / 12) === 0 }]}
      yAxis={[{ tickLabelStyle: TICK, valueFormatter: (v) => nf.format(v) }]}
      margin={{ left: 8, right: 8, top: 8, bottom: 4 }}
      hideLegend
      borderRadius={3}
      grid={{ horizontal: true }}
      sx={{ "& .MuiChartsGrid-line": { stroke: "rgba(255,255,255,0.06)" } }}
    />
  );
};

// Classifica orizzontale (top dettagli / IP): la barra più grande in alto.
const TopBars = ({ items, labelWidth = 110, maxLabel = 18, mono = false, onSelect, selected }) => {
  if (!items?.length) return <Empty />;
  const keys = items.map((i) => i.key || "(empty)");
  return (
    <BarChart
      layout="horizontal"
      height={26 * items.length + 36}
      series={[{ data: items.map((i) => i.n), color: BAR_COLOR, label: "Events", valueFormatter: (v) => nf.format(v ?? 0) }]}
      yAxis={[{
        scaleType: "band",
        data: keys,
        width: labelWidth,
        tickLabelStyle: { fontSize: 11, fill: colors.grey[300], fontFamily: mono ? MONO : undefined },
        valueFormatter: (v, ctx) => (ctx?.location === "tick" ? truncate(String(v), maxLabel) : String(v)),
        colorMap: selected ? { type: "ordinal", values: keys, colors: keys.map((k) => (k === selected ? palette.primary.light : `${BAR_COLOR}88`)) } : undefined,
      }]}
      xAxis={[{ tickLabelStyle: TICK, valueFormatter: (v) => nf.format(v) }]}
      margin={{ left: 4, right: 12, top: 4, bottom: 4 }}
      hideLegend
      borderRadius={3}
      onItemClick={onSelect ? (_, item) => onSelect(keys[item.dataIndex]) : undefined}
      sx={onSelect ? { "& .MuiBarElement-root": { cursor: "pointer" } } : undefined}
    />
  );
};

function describe(s) {
  switch (s.type) {
    case "fan_out": return <><Mono>{s.src_ip}</Mono> reaches <b>{nf.format(s.destinations)}</b> destinations</>;
    case "fan_in": return <><b>{nf.format(s.sources)}</b> sources reach <Mono>{s.dest_ip}</Mono></>;
    case "pair": return <><Mono>{s.src_ip}</Mono> → <Mono>{s.dest_ip}</Mono>, over and over</>;
    default: return <><b>{nf.format(s.sources)}</b> sources and <b>{nf.format(s.destinations)}</b> destinations</>;
  }
}

const EVENTS_PAGE_SIZE = 50;

const hostPort = (ip, port) => (port || port === 0 ? `${ip}:${port}` : ip);

// Eventi singoli del pattern (drill-down), dal più recente, a pagine. Il click su una
// riga apre l'evento completo nello stesso pannello laterale della tabella principale.
// Il genitore lo rimonta (key) quando cambia il restringimento, così la lista riparte da capo.
const PatternEvents = ({ s, request, narrow, timeZone }) => {
  const [page, setPage] = useState(0);
  const [state, setState] = useState({ loading: true, events: [], hasMore: false, error: "" });
  const [openEvent, setOpenEvent] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setState((st) => ({ ...st, loading: true, error: "" }));
    API.getSuppressionSuggestionEvents({
      ...request, signature: s.signature, src_ip: s.src_ip, dest_ip: s.dest_ip,
      narrow, page, pageSize: EVENTS_PAGE_SIZE,
    }, ctrl.signal)
      .then((res) => setState((st) => ({
        loading: false, error: "", hasMore: !!res.data.hasMore,
        events: page === 0 ? res.data.events : [...st.events, ...res.data.events],
      })))
      .catch((err) => {
        if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
        setState((st) => ({ ...st, loading: false, error: "Could not load the events of this pattern." }));
      });
    return () => ctrl.abort();
    // narrow è stabile per tutta la vita del componente (vedi key nel genitore)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.signature, s.src_ip, s.dest_ip, request, page]);

  const { events, loading, hasMore, error } = state;
  const head = { color: "text.secondary", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", backgroundColor: "#232323" };
  return (
    <Box>
      <Box sx={{ maxHeight: 340, overflow: "auto", mt: 1 }}>
        <Table size="small" stickyHeader sx={{ "& td, & th": { borderColor: "rgba(255,255,255,0.06)", fontSize: 12.5, py: 0.5 } }}>
          <TableHead>
            <TableRow>
              {["Time", "Source", "Destination", "Protocol", "App protocol"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((e, i) => (
              <TableRow key={e.id ?? `${e.timestamp}-${i}`} hover onClick={() => setOpenEvent(e)} selected={openEvent === e} sx={{ cursor: "pointer" }}>
                <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{dayjs(e.timestamp).tz(timeZone).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
                <TableCell><Mono>{hostPort(e.src_ip, e.src_port)}</Mono></TableCell>
                <TableCell><Mono>{hostPort(e.dest_ip, e.dest_port)}</Mono></TableCell>
                <TableCell>{e.proto ?? "–"}</TableCell>
                <TableCell>{e.app_proto && e.app_proto !== "failed" ? e.app_proto : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && !error && events.length === 0 && <Empty height={60} />}
      </Box>
      {error && <Typography sx={{ color: "error.light", fontSize: 13, py: 1 }}>{error}</Typography>}
      <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, mt: 1 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {nf.format(events.length)} event{events.length === 1 ? "" : "s"} shown, newest first
        </Typography>
        {loading && <CircularProgress size={16} />}
        {hasMore && !loading && <Button size="small" onClick={() => setPage((p) => p + 1)}>Load {EVENTS_PAGE_SIZE} more</Button>}
      </Stack>
      <LogSidePanel log={openEvent} open={Boolean(openEvent)} onClose={() => setOpenEvent(null)} />
    </Box>
  );
};

// Coppie sorgente/destinazione di un dettaglio diffuso: una regola per coppia è
// spesso più sicura di una regola su ANY/ANY che silenzia il dettaglio ovunque.
const PairList = ({ s, pairs, narrow, onNarrow, onCreateRule }) => (
  <Stack spacing={0.5} sx={{ mt: 1 }}>
    {pairs.map((p) => {
      const selected = narrow.src_ip === p.src_ip && narrow.dest_ip === p.dest_ip;
      return (
        <Box key={`${p.src_ip}|${p.dest_ip}`} sx={{
          display: "grid", gridTemplateColumns: "1fr 90px auto", gap: 1.5, alignItems: "center", px: 1, py: 0.5, borderRadius: 1,
          backgroundColor: selected ? "rgba(69,144,149,0.18)" : "transparent",
        }}>
          <Typography sx={{ fontSize: 13, minWidth: 0, wordBreak: "break-all" }}><Mono>{p.src_ip} → {p.dest_ip}</Mono></Typography>
          <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{nf.format(p.n)}</Typography>
          <Stack direction="row" spacing={1} sx={{ justifySelf: "end" }}>
            <Button size="small" onClick={() => onNarrow(selected ? {} : { src_ip: p.src_ip, dest_ip: p.dest_ip })} aria-pressed={selected}>
              {selected ? "All events" : "Events"}
            </Button>
            {p.covered ? (
              <Chip size="small" variant="outlined" label="Rule exists" sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
            ) : (
              <Button size="small" variant="outlined" onClick={() => onCreateRule({ ...s, src_ip: p.src_ip, dest_ip: p.dest_ip })}
                sx={{ borderColor: palette.primary.main, color: "#eaeaea" }}>
                Create rule
              </Button>
            )}
          </Stack>
        </Box>
      );
    })}
  </Stack>
);

// Grafici del singolo pattern, caricati alla prima apertura. Da qui si scende agli
// eventi singoli, eventualmente ristretti alla sorgente/destinazione/coppia cliccata.
const SuggestionDetail = ({ s, request, timeZone, onCreateRule }) => {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [showEvents, setShowEvents] = useState(false);
  const [narrow, setNarrow] = useState({});

  useEffect(() => {
    const ctrl = new AbortController();
    API.getSuppressionSuggestionDetail({ ...request, signature: s.signature, src_ip: s.src_ip, dest_ip: s.dest_ip }, ctrl.signal)
      .then((res) => setState({ loading: false, data: res.data, error: "" }))
      .catch((err) => {
        if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
        setState({ loading: false, data: null, error: "Could not load the details of this pattern." });
      });
    return () => ctrl.abort();
  }, [s.signature, s.src_ip, s.dest_ip, request]);

  if (state.loading) return <Box sx={{ py: 3, display: "grid", placeItems: "center" }}><CircularProgress size={22} /></Box>;
  if (state.error) return <Typography sx={{ color: "error.light", fontSize: 13, py: 1 }}>{state.error}</Typography>;

  const { data } = state;
  const color = TYPES[s.type].color;
  // Restringere ha senso solo dove la regola dice ANY: un IP già fissato ha una sola barra.
  const drill = (field) => (s[field] === "ANY" ? (k) => {
    setNarrow((n) => {
      const next = { ...n };
      if (n[field] === k) delete next[field]; else next[field] = k;
      return next;
    });
    setShowEvents(true);
  } : undefined);
  const narrowPair = (n) => { setNarrow(n); setShowEvents(true); };
  const narrowLabel = [narrow.src_ip && `source ${narrow.src_ip}`, narrow.dest_ip && `destination ${narrow.dest_ip}`].filter(Boolean).join(" · ");
  const pairs = s.type === "widespread" ? data.topPairs ?? [] : [];

  return (
    <Box sx={{ pt: 1.5, display: "grid", gap: 1.5 }}>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" } }}>
        <Panel title="Events of this pattern over time">
          <TimelineChart points={data.timeline} unit={data.window?.unit} timeZone={timeZone} height={150} color={color} />
        </Panel>
        <Panel title="Sources" subtitle={s.src_ip === "ANY" ? "Click a bar to see its events" : undefined}>
          <TopBars items={data.topSources} mono maxLabel={15} onSelect={drill("src_ip")} selected={narrow.src_ip} />
        </Panel>
        <Panel title="Destinations" subtitle={s.dest_ip === "ANY" ? "Click a bar to see its events" : undefined}>
          <TopBars items={data.topDestinations} mono maxLabel={15} onSelect={drill("dest_ip")} selected={narrow.dest_ip} />
        </Panel>
      </Box>

      {pairs.length > 0 && (
        <Panel title="Top source/destination pairs"
          subtitle="A rule per pair silences only these flows instead of this detail everywhere">
          <PairList s={s} pairs={pairs} narrow={narrow} onNarrow={narrowPair} onCreateRule={onCreateRule} />
        </Panel>
      )}

      <Panel title="Single events" subtitle={narrowLabel ? `Only ${narrowLabel}` : "All events of this pattern in the selected range. Click an event to see it in full"}>
        <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Button size="small" startIcon={<ListAltOutlinedIcon fontSize="small" />} onClick={() => setShowEvents((v) => !v)} aria-expanded={showEvents}
            sx={{ color: "#eaeaea", border: "1px solid rgba(255,255,255,0.14)", "&:hover": { borderColor: palette.primary.light } }}>
            {showEvents ? "Hide events" : "Show events"}
          </Button>
          {narrowLabel && (
            <Chip size="small" color="primary" variant="outlined" label={narrowLabel} onDelete={() => setNarrow({})} />
          )}
        </Stack>
        <Collapse in={showEvents} unmountOnExit>
          <PatternEvents key={`${narrow.src_ip ?? ""}|${narrow.dest_ip ?? ""}`} s={s} request={request} narrow={narrow} timeZone={timeZone} />
        </Collapse>
      </Panel>
    </Box>
  );
};

const fmtWindow = (request, timeZone) =>
  `${dayjs(request.dateFrom).tz(timeZone).format("DD/MM/YYYY HH:mm")} – ${dayjs(request.dateTo).tz(timeZone).format("DD/MM/YYYY HH:mm")}`;

const ackPayload = (request, target) => ({ ...request, signature: target.signature, src_ip: target.src_ip, dest_ip: target.dest_ip });

const isAbort = (err) => err?.name === "AbortError" || err?.code === "ERR_CANCELED";

// Conferma dell'ack massivo: prima conta gli eventi coinvolti, poi li marca tutti.
const BulkAckDialog = ({ target, request, timeZone, onClose, onDone }) => {
  const [state, setState] = useState({ loading: true, events: null, error: "", saving: false });

  useEffect(() => {
    if (!target) return undefined;
    const ctrl = new AbortController();
    setState({ loading: true, events: null, error: "", saving: false });
    API.previewBulkAck(ackPayload(request, target), ctrl.signal)
      .then((res) => setState({ loading: false, events: res.data.events, error: "", saving: false }))
      .catch((err) => {
        if (isAbort(err)) return;
        setState({
          loading: false, events: null, saving: false,
          error: err?.response?.status === 408 ? "Counting took too long. Choose a shorter time range." : "Could not count the events. Try again.",
        });
      });
    return () => ctrl.abort();
  }, [target, request]);

  const confirm = () => {
    setState((st) => ({ ...st, saving: true, error: "" }));
    API.bulkAck(ackPayload(request, target))
      .then((res) => onDone({ ...res.data, target }))
      .catch((err) => setState((st) => ({
        ...st, saving: false,
        error: err?.response?.status === 408
          ? "It took too long. Nothing was acknowledged: choose a shorter time range."
          : "Could not acknowledge the events. Nothing was changed.",
      })));
  };

  const { loading, events, error, saving } = state;
  return (
    <Dialog open={Boolean(target)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm"
      slotProps={{ paper: { sx: { backgroundColor: "#1b1b1b" } } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Acknowledge all events</DialogTitle>
      <DialogContent>
        {target && (
          <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)", backgroundColor: "#232323", mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, wordBreak: "break-word" }}>{target.signature}</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
              Source <Mono>{target.src_ip}</Mono> · destination <Mono>{target.dest_ip}</Mono>
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{fmtWindow(request, timeZone)}</Typography>
          </Box>
        )}
        {loading && <Box sx={{ py: 2, display: "grid", placeItems: "center" }}><CircularProgress size={22} /></Box>}
        {!loading && events !== null && (
          events === 0
            ? <Typography sx={{ fontSize: 14 }}>No events to acknowledge in this range.</Typography>
            : (
              <>
                <Typography sx={{ fontSize: 14 }}>
                  <b>{nf.format(events)}</b> event{events === 1 ? "" : "s"} in this range will be marked as acknowledged.
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>
                  Events already acknowledged stay as they are. Only events received so far are included: new ones will not be
                  acknowledged automatically. You can undo right after.
                </Typography>
              </>
            )
        )}
        {error && <Typography sx={{ color: "error.light", fontSize: 13, mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={loading || saving || !events}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <DoneAllIcon fontSize="small" />}>
          {saving ? "Acknowledging…" : events ? `Acknowledge ${nf.format(events)}` : "Acknowledge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Esito dell'ultimo ack massivo, con l'annullamento a portata di mano.
const BulkAckResult = ({ result, onUndo, onDismiss }) => {
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState("");
  if (!result) return null;
  const undo = () => {
    setUndoing(true);
    setError("");
    onUndo(result).catch(() => setError("Could not undo. Try again.")).finally(() => setUndoing(false));
  };
  const label = truncate(result.target.signature, 60);
  let text;
  if (result.undone) text = <>Undone: {nf.format(result.removed)} acknowledgment{result.removed === 1 ? "" : "s"} removed from “{label}”.</>;
  else if (!result.acked) text = <>All the events of “{label}” in this range were already acknowledged.</>;
  else text = <>Acknowledged <b>{nf.format(result.acked)}</b> event{result.acked === 1 ? "" : "s"} of “{label}”.</>;
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${palette.primary.main}66`, backgroundColor: "rgba(69,144,149,0.12)",
      display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <DoneAllIcon fontSize="small" sx={{ color: palette.primary.light }} />
      <Typography sx={{ fontSize: 13.5, flex: 1, minWidth: 200 }}>{text}</Typography>
      {error && <Typography sx={{ color: "error.light", fontSize: 12.5 }}>{error}</Typography>}
      {result.bulk_id && !result.undone && (
        <Button size="small" onClick={undo} disabled={undoing}>{undoing ? "Undoing…" : "Undo"}</Button>
      )}
      <IconButton size="small" aria-label="Dismiss" onClick={onDismiss}><CloseIcon fontSize="small" /></IconButton>
    </Box>
  );
};

// Segnala il pattern su cui conviene lavorare: da solo pesa una quota rilevante degli eventi.
const SuggestedChip = ({ share, sx }) => (
  <Tooltip arrow title={`This pattern alone is ${pct(share)} of the events in this period. Suppressing it, or fixing its cause, removes most of the noise.`}>
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.7, px: 0.9, py: 0.2, borderRadius: 1, cursor: "default", ...sx,
      color: palette.primary.light, backgroundColor: "rgba(69,144,149,0.18)", border: `1px solid ${palette.primary.main}` }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: palette.primary.light }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Suggested</Typography>
    </Box>
  </Tooltip>
);

const SuggestionRow = ({ s, onCreateRule, onAck, timeZone, request, hidden, onToggleHidden }) => {
  const [open, setOpen] = useState(false);
  const t = TYPES[s.type];
  const Icon = t.icon;
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: open ? `${t.color}66` : s.suggested ? `${palette.primary.main}99` : "rgba(255,255,255,0.08)", backgroundColor: "#232323" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "40px 1fr", md: "40px 1fr auto 170px auto" }, gap: 2, alignItems: "center" }}>
        <Tooltip arrow title={t.label}>
          <Box sx={{ width: 36, height: 36, borderRadius: "8px", display: "grid", placeItems: "center", color: t.color, backgroundColor: `${t.color}1f`, border: `1px solid ${t.color}66` }}>
            <Icon fontSize="small" />
          </Box>
        </Tooltip>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, wordBreak: "break-word" }}>{s.signature}</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{describe(s)}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "rgba(234,234,234,0.6)" }}>
            Rule: source <Mono>{s.src_ip}</Mono> · destination <Mono>{s.dest_ip}</Mono> · detail contains “{s.signature}”
          </Typography>
          {s.suggested && <SuggestedChip share={s.share} sx={{ mt: 0.6, display: { xs: "inline-flex", md: "none" } }} />}
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" } }}>
          {s.suggested && <SuggestedChip share={s.share} />}
        </Box>

        <Box sx={{ gridColumn: { xs: "2", md: "auto" } }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{nf.format(s.events)}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>events · {pct(s.share)} of total</Typography>
          <LinearProgress variant="determinate" value={Math.min(100, s.share * 100)}
            sx={{ mt: 0.5, height: 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { backgroundColor: t.color } }} />
          {s.lastSeen && (
            <Typography sx={{ mt: 0.5, fontSize: 11, color: "text.secondary" }}>
              Last seen {dayjs(s.lastSeen).tz(timeZone).format("DD/MM HH:mm")}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ gridColumn: { xs: "2", md: "auto" }, justifySelf: { md: "end" } }}>
          <Button size="small" startIcon={<InsightsIcon fontSize="small" />} onClick={() => setOpen((o) => !o)} aria-expanded={open}
            sx={{ color: "#eaeaea", border: "1px solid rgba(255,255,255,0.14)", "&:hover": { borderColor: palette.primary.light } }}>
            {open ? "Hide" : "Details"}
          </Button>
          <Tooltip arrow title={hidden
            ? "Hidden from the table view. Click to show these events again."
            : "Hide these events from the table view without creating a suppression rule"}>
            <Button size="small" startIcon={<VisibilityOffOutlinedIcon fontSize="small" />} onClick={() => onToggleHidden(s)} aria-pressed={hidden}
              sx={{
                color: hidden ? "#eaeaea" : "rgba(234,234,234,0.8)",
                border: "1px solid",
                borderColor: hidden ? palette.primary.main : "rgba(255,255,255,0.14)",
                backgroundColor: hidden ? "rgba(69,144,149,0.18)" : "transparent",
                "&:hover": { borderColor: palette.primary.light },
              }}>
              {hidden ? "Hidden" : "Hide from view"}
            </Button>
          </Tooltip>
          <Tooltip arrow title="Acknowledge all the events of this pattern in the selected range">
            <Button size="small" startIcon={<DoneAllIcon fontSize="small" />} onClick={() => onAck(s)}
              sx={{ color: "#eaeaea", border: "1px solid rgba(255,255,255,0.14)", "&:hover": { borderColor: palette.primary.light } }}>
              Ack all
            </Button>
          </Tooltip>
          <Button size="small" variant="outlined" onClick={() => onCreateRule(s)}
            sx={{ borderColor: palette.primary.main, color: "#eaeaea", "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" } }}>
            Create rule
          </Button>
        </Stack>
      </Box>
      <Collapse in={open} unmountOnExit>
        <SuggestionDetail s={s} request={request} timeZone={timeZone} onCreateRule={onCreateRule} />
      </Collapse>
    </Box>
  );
};

// Pattern nascosti dalla vista della tabella: si attivano/disattivano insieme dal
// toolbar ("Hide noise") e si tolgono uno per uno qui.
const HiddenPatterns = ({ noise, onChangeNoise }) => {
  if (!noise?.patterns?.length) return null;
  const remove = (p) => onChangeNoise({ ...noise, patterns: noise.patterns.filter((x) => patternKey(x) !== patternKey(p)) });
  return (
    <Panel
      title={`Hidden from view · ${noise.patterns.length}`}
      subtitle="These events are removed from the table without a suppression rule: they are still collected and notified. Only you see this filter."
    >
      <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={noise.enabled} onChange={(e) => onChangeNoise({ ...noise, enabled: e.target.checked })} />}
          label={<Typography sx={{ fontSize: 13 }}>{noise.enabled ? "Hiding them in the table" : "Shown in the table"}</Typography>}
          sx={{ mr: 1 }}
        />
        {noise.patterns.map((p) => (
          <Chip
            key={patternKey(p)}
            size="small"
            variant="outlined"
            icon={<CleaningServicesOutlinedIcon sx={{ fontSize: 14 }} />}
            label={<><Mono>{p.src_ip} → {p.dest_ip}</Mono> · {truncate(p.signature, 40)}</>}
            title={p.signature}
            onDelete={() => remove(p)}
            sx={{ borderColor: "rgba(255,255,255,0.15)", maxWidth: 460 }}
          />
        ))}
        <Button size="small" onClick={() => onChangeNoise({ enabled: false, patterns: [] })}>Clear all</Button>
      </Stack>
    </Panel>
  );
};

const StatTile = ({ label, value, hint }) => (
  <Box sx={{ px: 1.5, py: 1, borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)", backgroundColor: "#232323", minWidth: 150 }}>
    <Typography sx={{ fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", color: "text.secondary" }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{hint}</Typography>}
  </Box>
);

const SuppressionSuggestions = ({ open, onClose, tipo, onCreateRule, refreshKey = 0, noise, onChangeNoise, onAcked }) => {
  const [range, setRange] = useState("7d");
  const [minEvents, setMinEvents] = useState(20);
  const [typeFilter, setTypeFilter] = useState("all");
  const [signatureFilter, setSignatureFilter] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [ackTarget, setAckTarget] = useState(null);
  const [excludeAcked, setExcludeAcked] = useState(false);
  const [ackResult, setAckResult] = useState(null);
  const timeZone = window.sessionStorage.getItem("timezone") || "Etc/GMT";

  // Stessa finestra per panoramica e dettagli, fissata al momento del caricamento.
  const request = useMemo(() => {
    const hours = RANGES.find((r) => r.value === range)?.hours ?? 168;
    const dateTo = dayjs();
    return { tipo, timezone: timeZone, dateFrom: dateTo.subtract(hours, "hour").toISOString(), dateTo: dateTo.toISOString(), excludeAcked };
    // reload/refreshKey spostano la finestra a "adesso" quando si ricarica
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, range, timeZone, reload, refreshKey, open, excludeAcked]);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    setError("");
    API.getSuppressionSuggestions({ ...request, minEvents }, ctrl.signal)
      .then((res) => setData(res.data))
      .catch((err) => {
        if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
        setData(null);
        setError(err?.response?.status === 408
          ? "The analysis took too long. Choose a shorter time range."
          : "Could not load suggestions. Try again.");
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [open, request, minEvents]);

  const suggestions = useMemo(() => data?.suggestions ?? [], [data]);
  const counts = useMemo(() => suggestions.reduce((acc, s) => ({ ...acc, [s.type]: (acc[s.type] || 0) + 1 }), {}), [suggestions]);
  const visible = suggestions.filter((s) =>
    (typeFilter === "all" || s.type === typeFilter) && (!signatureFilter || s.signature === signatureFilter));
  // Le regole possono sovrapporsi (una coppia sta anche nel dettaglio diffuso), quindi è un massimo.
  const topShare = Math.min(1, suggestions.slice(0, 5).reduce((sum, s) => sum + s.share, 0));
  const stats = data?.stats;
  const hiddenKeys = useMemo(() => new Set((noise?.patterns ?? []).map(patternKey)), [noise]);
  // Aggiungere un pattern attiva il filtro: chi clicca "Hide from view" si aspetta di non vederlo più.
  const toggleHidden = (s) => {
    const key = patternKey(s);
    const patterns = noise?.patterns ?? [];
    if (hiddenKeys.has(key)) {
      onChangeNoise({ ...noise, patterns: patterns.filter((p) => patternKey(p) !== key) });
    } else {
      onChangeNoise({
        enabled: true,
        patterns: [...patterns, {
          signature: s.signature, src_ip: s.src_ip, dest_ip: s.dest_ip, type: s.type, events: s.events,
          addedAt: new Date().toISOString(),
        }],
      });
    }
  };
  const severity = SEVERITY_LABEL[tipo] ?? "";

  // Un dettaglio di "Top details" è un pattern senza vincoli sugli IP.
  const ackDetail = (signature) => setAckTarget({ signature, src_ip: "ANY", dest_ip: "ANY" });
  const ackPattern = (s) => setAckTarget({ signature: s.signature, src_ip: s.src_ip, dest_ip: s.dest_ip });
  // Con gli acked esclusi, un ack massivo (o il suo annullamento) cambia l'analisi stessa.
  const afterAckChange = () => {
    onAcked?.();
    if (excludeAcked) setReload((r) => r + 1);
  };
  const handleAckDone = (result) => {
    setAckTarget(null);
    setAckResult(result);
    if (result.acked) afterAckChange();
  };
  const handleUndo = (result) => API.undoBulkAck({ bulk_id: result.bulk_id }).then((res) => {
    setAckResult({ ...result, undone: true, removed: res.data.removed });
    afterAckChange();
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" slotProps={{ paper: { sx: { backgroundColor: "#1b1b1b" } } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{severity} noise analysis</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: "90ch" }}>
          Statistics on the {severity.toLowerCase()} events that are not suppressed yet, and the recurring patterns a single
          log suppression rule could silence. Patterns already covered by a rule are left out.
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <BulkAckDialog target={ackTarget} request={request} timeZone={timeZone} onClose={() => setAckTarget(null)} onDone={handleAckDone} />
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2, pt: 1 }}>
          {RANGES.map((r) => (
            <Chip key={r.value} label={r.label} onClick={() => setRange(r.value)}
              color={range === r.value ? "primary" : "default"} variant={range === r.value ? "filled" : "outlined"} />
          ))}
          <TextField select size="small" label="Min. events per pattern" value={minEvents}
            onChange={(e) => setMinEvents(Number(e.target.value))} sx={{ width: 190 }}>
            {MIN_EVENTS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <Tooltip arrow title={excludeAcked
            ? "Acknowledged events are left out of statistics, suggestions and details. Click to include them."
            : "Acknowledged events are counted too. Click to analyse only the ones not acknowledged yet."}>
            <FormControlLabel
              control={<Switch size="small" checked={excludeAcked} onChange={(e) => setExcludeAcked(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13 }}>Exclude acknowledged</Typography>}
              sx={{ ml: 0.5 }}
            />
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Tooltip arrow title="Run the analysis again">
            <span>
              <IconButton onClick={() => setReload((r) => r + 1)} disabled={loading} aria-label="Refresh"><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Typography sx={{ color: "error.light", mb: 2 }}>{error}</Typography>}

        {data && !error && (
          <Stack spacing={2}>
            <BulkAckResult result={ackResult} onUndo={handleUndo} onDismiss={() => setAckResult(null)} />
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
              <StatTile label="Events analysed" value={nf.format(data.total)} hint={excludeAcked ? "not suppressed, not acknowledged" : "not suppressed yet"} />
              <StatTile label="Suggestions" value={nf.format(suggestions.length)} hint={`at least ${minEvents} events each`} />
              <StatTile label="Top 5 would remove" value={`≤ ${pct(topShare)}`} hint="of the events analysed" />
              {data.acked && (
                <StatTile label="Acknowledged" value={nf.format(data.acked.events)}
                  hint={`${pct(data.acked.total ? data.acked.events / data.acked.total : 0)} of ${nf.format(data.acked.total)} events${excludeAcked ? " · left out" : ""}`} />
              )}
            </Stack>

            {stats && (
              <>
                <Panel title="Events over time" subtitle={data.window?.unit === "hour" ? "Per hour" : "Per day"}>
                  <TimelineChart points={stats.timeline} unit={data.window?.unit} timeZone={timeZone} />
                </Panel>
                <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" } }}>
                  <Panel title="Top details" subtitle="Click a bar to show only its suggestions">
                    <TopBars items={stats.topSignatures} labelWidth={230} maxLabel={36} selected={signatureFilter}
                      onSelect={(k) => setSignatureFilter((cur) => (cur === k ? null : k))} />
                  </Panel>
                  <Panel title="Top sources"><TopBars items={stats.topSources} mono maxLabel={15} /></Panel>
                  <Panel title="Top destinations"><TopBars items={stats.topDestinations} mono maxLabel={15} /></Panel>
                </Box>
              </>
            )}

            {onChangeNoise && <HiddenPatterns noise={noise} onChangeNoise={onChangeNoise} />}

            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Suggested rules</Typography>
              {suggestions.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                  <Chip size="small" label={`All · ${suggestions.length}`} onClick={() => setTypeFilter("all")}
                    variant={typeFilter === "all" ? "filled" : "outlined"} color={typeFilter === "all" ? "primary" : "default"} />
                  {Object.entries(TYPES).filter(([k]) => counts[k]).map(([k, t]) => {
                    const Icon = t.icon;
                    return (
                      <Chip key={k} size="small" icon={<Icon sx={{ fontSize: 16, color: `${t.color} !important` }} />}
                        label={`${t.short} · ${counts[k]}`} onClick={() => setTypeFilter(k)}
                        variant={typeFilter === k ? "filled" : "outlined"}
                        sx={typeFilter === k ? { backgroundColor: `${t.color}33` } : undefined} />
                    );
                  })}
                  {signatureFilter && (
                    <>
                      <Chip size="small" color="primary" variant="outlined" label={`Detail: ${truncate(signatureFilter, 40)}`}
                        onDelete={() => setSignatureFilter(null)} />
                      <Tooltip arrow title="Acknowledge all the events with this detail in the selected range, from any source to any destination">
                        <Button size="small" startIcon={<DoneAllIcon fontSize="small" />} onClick={() => ackDetail(signatureFilter)}>
                          Ack all events of this detail
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              )}

              {suggestions.length === 0 && !loading && (
                <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
                  No recurring patterns above {minEvents} events in this range. Try a longer range or a lower minimum.
                </Box>
              )}
              {suggestions.length > 0 && visible.length === 0 && (
                <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>No suggestions match the selected filters.</Box>
              )}
              <Stack spacing={1}>
                {visible.map((s) => (
                  <SuggestionRow key={s.id} s={s} onCreateRule={onCreateRule} onAck={ackPattern} timeZone={timeZone} request={request}
                    hidden={hiddenKeys.has(patternKey(s))} onToggleHidden={toggleHidden} />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuppressionSuggestions;
